import { yupResolver } from '@hookform/resolvers/yup';
import { IonButton, IonButtons, IonContent, IonHeader, IonIcon, IonTitle, IonToolbar } from '@ionic/react';
import { arrowBack } from 'ionicons/icons';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { unwrapResult } from '@reduxjs/toolkit';
import moment from 'moment';
import { logTransaction } from '../../common/log';
import { getStateDateAsString, getStateStringAsMoment } from '../../common/util';
import { ClockingDirection } from '../../models/dms/clocking.model';
import { UNKNOWN_TECH } from '../../models/dms/technician.model';
import { IThunkError } from '../../models/error.model';
import { LogType } from '../../models/log.model';
import { useClockOffLabourModal } from '../../providers/ClockOffLabourModalProvider';
import { useDataSync } from '../../providers/DataSyncProvider';
import { useToast } from '../../providers/ToastProvider';
import store from '../../store';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { clockOffAttendanceClockingAsync } from '../../store/technicianClockingSlice';
import { IonModalWrapper } from './ClockOffAttendanceModal.style';
import FormDatePickerButton from '../form/formDatePickerButton/FormDatePickerButton';

export interface IComponentProps {
  showModal: boolean;
  handleDismiss?: () => void;
}

interface IFormInputs {
  endDateTime: string;
  endDateTimeBreak: string;
}

const schema = yup.object().shape({});

const ClockOffAttendanceModal: React.FC<IComponentProps> = function ({ showModal, handleDismiss }) {
  let submitButtonRef: HTMLButtonElement | null = null;
  const [finishTimeMin, setFinishTimeMin] = useState(moment());
  const [finishTimeMax, setFinishTimeMax] = useState(moment());
  const {
    state: { requestQueueSyncTimer },
  } = useDataSync();
  const {
    actions: { errorToast },
  } = useToast();
  const {
    actions: { showClockOffLabourModal },
  } = useClockOffLabourModal();

  const { technician } = useAppSelector((state) => state.auth);
  const { labourClocking, attendanceClocking } = useAppSelector((state) => state.technicianClocking);

  const dispatch = useAppDispatch();

  const { control, handleSubmit, setValue, getValues, reset } = useForm<IFormInputs>({
    defaultValues: {
      endDateTime: moment().toISOString(true),
    },
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (showModal) {
      setFinishTimeMin(getStateStringAsMoment(attendanceClocking?.startDateTime));
      setValue('endDateTime', moment().toISOString(true));
      setValue('endDateTimeBreak', moment().toISOString(true));
    } else {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  const onSubmit = handleSubmit((data) => {
    if (labourClocking?.direction === ClockingDirection.On) {
      showClockOffLabourModal(labourClocking.jobData, labourClocking?.repairOrder, true, moment(data.endDateTime, moment.ISO_8601).toDate(), handleDismiss);
    } else {
      clockOffAttendance();
    }
  });

  const clockOffAttendance = async () => {
    const { lastPosition } = store.getState().location;
    const startDateTime = getStateStringAsMoment(attendanceClocking?.startDateTime);
    let endDateTime = moment(getValues().endDateTime);

    // Ensure endDateTime is no less than start date/time
    if (endDateTime.isBefore(startDateTime)) {
      endDateTime = moment(startDateTime);
    }

    try {
      logTransaction(LogType.Info, `Clock out attendance requested`);
      handleDismiss && handleDismiss();
      const resultAction = await dispatch(
        clockOffAttendanceClockingAsync({
          techCode: technician?.code || UNKNOWN_TECH,
          endDateTime: getStateDateAsString(endDateTime.toDate()),
          position: lastPosition,
        }),
      );
      unwrapResult(resultAction);
      requestQueueSyncTimer?.fire();
    } catch (ex) {
      const err = (ex as IThunkError).message;
      logTransaction(LogType.Error, `Failed to clock out attendance. ${err}`);
      errorToast(err);
    }
  };

  const handleOpenDateModal = (isOpen: boolean) => {
    if (isOpen) {
      setFinishTimeMax(moment());
    }
  };

  return (
    <>
      <IonModalWrapper
        className="stacked-modal"
        isOpen={showModal}
        onDidDismiss={() => {
          if (showModal) {
            handleDismiss && handleDismiss();
          }
        }}>
        <IonHeader color="lightest">
          <IonToolbar color="primary">
            <IonButtons slot="start">
              <IonButton fill="solid" onClick={() => handleDismiss && handleDismiss()}>
                <IonIcon icon={arrowBack} />
              </IonButton>
            </IonButtons>
            <IonTitle>Clock Out Attendance?</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent color="lightest" className="ion-padding">
          {attendanceClocking?.direction === ClockingDirection.On && (
            <div>
              Are you sure you want to clock out attendance?
              <form id="clockOffAttendanceForm" noValidate onSubmit={onSubmit}>
                <button
                  type="submit"
                  className="ion-hide"
                  ref={(button) => {
                    if (!submitButtonRef && button) {
                      submitButtonRef = button;
                    }
                  }}>
                  Submit
                </button>
                <FormDatePickerButton
                  control={control}
                  datetimeId="attendancefinishtime1"
                  label="Finish Time"
                  placeholder="Finish Time"
                  name="endDateTime"
                  min={finishTimeMin.toISOString(true)}
                  max={finishTimeMax.toISOString(true)}
                  handleModalOpen={(isOpen) => handleOpenDateModal(isOpen)}
                />
              </form>
              <IonButton expand="block" color="warning" onClick={() => submitButtonRef?.click()}>
                Clock Out
              </IonButton>
            </div>
          )}
          {attendanceClocking?.direction === ClockingDirection.Break && (
            <div>
              <div className="ion-padding">Are you sure you want to clock out attendance?</div>
              <form id="clockOffAttendanceForm" noValidate onSubmit={onSubmit}>
                <button
                  type="submit"
                  className="ion-hide"
                  ref={(button) => {
                    if (!submitButtonRef && button) {
                      submitButtonRef = button;
                    }
                  }}>
                  Submit
                </button>
                <FormDatePickerButton control={control} datetimeId="attendancefinishtime2" label="Finish Time" placeholder="Finish Time" name="endDateTimeBreak" disabled />
              </form>
              <IonButton expand="block" color="warning" onClick={() => submitButtonRef?.click()}>
                Clock Out
              </IonButton>
            </div>
          )}
        </IonContent>
      </IonModalWrapper>
    </>
  );
};

export default ClockOffAttendanceModal;
